import type { ClientState, PortfolioHealth } from '@ops/contracts'
import { CLIENT_STATE_BY_RISK, CLIENT_STATE_LABELS } from '@ops/contracts'

import { formatMetricValue } from '../../lib/format'
import { Panel, ShareBar, type ShareSegment } from '../../ui'
import { segmentosDeUnEje, Vacio } from '../comunes'

/**
 * Salud del portfolio: cómo se reparten las cuentas por estado, **ordenadas por
 * riesgo** y no alfabéticamente ni por tamaño. El orden lo define el contrato
 * (`CLIENT_STATE_BY_RISK`), así que la barra empieza por lo que está trabado.
 *
 * Dos estados comparten el rojo (pendiente de implementación y retención) porque
 * los dos son riesgo, así que el segundo va con `trama`: el mapeo con la leyenda
 * queda 1 a 1 sin depender del color.
 */
const SEVERIDAD_POR_ESTADO: Record<ClientState, ShareSegment['severidad']> = {
  pendiente_implementacion: 'critical',
  retencion: 'critical',
  implementacion: 'warning',
  expansion: 'warning',
  qbr: 'ok',
}

/** El segundo estado de cada color lleva trama. */
const TRAMA_POR_ESTADO: Record<ClientState, boolean> = {
  pendiente_implementacion: false,
  retencion: true,
  implementacion: false,
  expansion: true,
  qbr: false,
}

/**
 * Los segmentos de la barra, con el porcentaje **derivado** de la misma cantidad
 * de cuentas que la leyenda imprime al lado (invariante de un solo eje, ver
 * `modules/comunes/share.ts`). `fila.pctPortfolio` es el mismo número calculado
 * por el servidor; usarlo acá dejaba dos campos independientes pegados en la
 * misma frase, que es exactamente cómo se produjo el bug del split de revenue.
 */
export function segmentosDePortfolio(
  portfolio: PortfolioHealth,
): ShareSegment[] {
  return segmentosDeUnEje(
    CLIENT_STATE_BY_RISK.flatMap((estado) => {
      const fila = portfolio.porEstado.find((e) => e.estado === estado)
      if (fila === undefined || fila.cantidad === 0) return []
      return [
        {
          id: estado,
          label: CLIENT_STATE_LABELS[estado],
          cantidad: fila.cantidad,
          severidad: SEVERIDAD_POR_ESTADO[estado],
          trama: TRAMA_POR_ESTADO[estado],
        },
      ]
    }),
    portfolio.total,
  )
}

/** Porcentaje del portfolio sin implementar o en retención. */
export function friccionPct(portfolio: PortfolioHealth): number | null {
  if (portfolio.total <= 0) return null
  const trabadas = portfolio.porEstado
    .filter(
      (e) =>
        e.estado === 'pendiente_implementacion' || e.estado === 'retencion',
    )
    .reduce((total, e) => total + e.cantidad, 0)
  return (trabadas / portfolio.total) * 100
}

export interface PortfolioPanelProps {
  portfolio: PortfolioHealth
}

export function PortfolioPanel({ portfolio }: PortfolioPanelProps) {
  const segmentos = segmentosDePortfolio(portfolio)
  const friccion = friccionPct(portfolio)

  return (
    <Panel
      titulo="Dónde está trabado el portfolio"
      subtitulo={`${formatMetricValue(portfolio.total, 'count')} cuentas, ordenadas por riesgo`}
      padding="sm"
      footer={
        friccion === null
          ? 'Sin cuentas cargadas no hay fricción que medir.'
          : `Fricción: el ${formatMetricValue(friccion, 'percent')} del portfolio está sin implementar o en retención.`
      }
    >
      {segmentos.length === 0 ? (
        <Vacio icono={null}
          mensaje="Todavía no hay cuentas en el portfolio"
          detalle="Cuando se cargue la primera cuenta, su estado aparece en esta barra."
          punteada
        />
      ) : (
        <ShareBar
          segmentos={segmentos}
          label="Portfolio por estado, ordenado por riesgo"
          total={portfolio.total}
        />
      )}
    </Panel>
  )
}
